import { BusinessServices, IUser } from '@shs-digi-care-monorepo/interfaces';
import { streamsOf } from './user-role.util';

/** The regional MDLA applications a user can be sent to. */
export type Region = 'emea' | 'nam';

const BASE_DOMAIN = 'my-digital-lab-assistant.siemens-healthineers.com';
const REGIONS: Region[] = ['emea', 'nam'];

const REGION_BY_STREAM: Partial<Record<BusinessServices, Region>> = {
    [BusinessServices.OM_EMEA]: 'emea',
    [BusinessServices.OM_NAM]: 'nam'
};

/**
 * URL of the given regional application. The applications of a stage differ in
 * their region label only – `<stage>.emea.<base>` and `emea.<base>` on prod – so
 * swapping that label keeps the user on the stage they came from. When the host
 * has no region label yet – e.g. `qa.<base>` or a branch test env's `<hash>.<base>` –
 * the region is inserted while keeping whatever stage/hash prefix is present.
 * Falls back to the dev environment when the app is not served from a known host,
 * which is the case during local development.
 *
 * @example
 * getRegionUrl('nam') // on qa.emea.<base> returns 'https://qa.nam.<base>'
 * getRegionUrl('nam') // on qa.<base> returns 'https://qa.nam.<base>'
 * getRegionUrl('nam') // on <hash>.<base> returns 'https://<hash>.nam.<base>'
 */
export function getRegionUrl(region: Region, hostname: string = window.location.hostname): string {
    const labels = hostname.split('.');
    const regionIndex = labels.findIndex((label) => REGIONS.includes(label as Region));

    if (regionIndex !== -1) {
        labels[regionIndex] = region;

        return `https://${labels.join('.')}`;
    }

    const isKnownHost = hostname === BASE_DOMAIN || hostname.endsWith(`.${BASE_DOMAIN}`);
    const prefixLabels = isKnownHost ? labels.slice(0, labels.length - BASE_DOMAIN.split('.').length) : ['dev'];

    return `https://${[...prefixLabels, region, BASE_DOMAIN].join('.')}`;
}

/**
 * The region a user belongs to when they open the application of `stream` without
 * holding a role in it. `null` whenever the user may use the application, either
 * because they hold a role in its stream or because their roles do not point to
 * the other region.
 *
 * Users without any stream bound role – profiles that are still served with the
 * deprecated flat role list – are never locked out.
 */
export function wrongRegionOf(user: IUser | null | undefined, stream: BusinessServices): Region | null {
    const streams = streamsOf(user);

    if (!streams.length || streams.includes(stream)) {
        return null;
    }

    const otherStream = stream === BusinessServices.OM_NAM ? BusinessServices.OM_EMEA : BusinessServices.OM_NAM;

    return streams.includes(otherStream) ? (REGION_BY_STREAM[otherStream] ?? null) : null;
}
